/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Headline, Paragraph, Title, Dropdown, TextField, Button } from '@cambridgeassessment/cambridge-ui';
import { RootState } from '../../../store';
import { StyledInputContainer } from '../ReplacementFiles/ReplacementFiles.styles';
import { Series } from '../../../types/Series';
import * as thunks from '../../../store/malpracticeReports/thunks';
import { isEmpty, find } from 'lodash';
import Loader from '../../../components/Loader/Loader';
import { DropdownContainer } from '../Content.styles';
import { PlagiarismAssessment, SearchQuery } from '../../../types/Plagiarism';
import { EntryComponent } from '@cambridgeassessment/lib-ee-commons';
import { useHistory } from 'react-router-dom';
import { displayNotification } from '../../../store/notification/actions';
import qs from 'qs';

const renderSeries = (item: Series): string => item.name;
const renderAssessments = (assessment: PlagiarismAssessment): string => {
  return `${assessment.code} - ${assessment.name}`;
};
const renderComponents = (component: EntryComponent): string => `${component.name} - ${component.number}`;

interface Props {
  defaultSearchQuery: Partial<Omit<SearchQuery, 'sectionId'>>;
  series: Series[];
}

const MalpracticeReports: React.FC<Props> = ({ defaultSearchQuery, series }): React.ReactElement => {
  const dispatch = useDispatch();
  const {
    malpracticeReport: { search },
  } = useSelector((state: RootState) => state);

  const [selectedSeries, setSelectedSeries] = useState<Series | undefined>(
    find(series, { id: Number(defaultSearchQuery?.seriesId) }),
  );
  const [selectedAssessment, setSelectedAssessment] = useState<PlagiarismAssessment | undefined>(
    find(search.assessments, { code: defaultSearchQuery?.assessmentCode }),
  );
  const [selectedComponent, setSelectedComponent] = useState<EntryComponent | undefined>(
    find(search.components, { number: defaultSearchQuery?.componentNumber }),
  );

  const [centreId, setCentreId] = useState<string>(defaultSearchQuery?.centreId || '');
  const [learnerNumber, setLearnerNumber] = useState<string>(defaultSearchQuery?.learnerNumber || '');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    if (selectedSeries) {
      dispatch(thunks.getAssessmentData(selectedSeries.id));
    }
  }, [dispatch, selectedSeries]);

  useEffect(() => {
    if (selectedAssessment) {
      dispatch(thunks.getAssessmentComponentsData((selectedSeries as Series).id, selectedAssessment.code));
    }
  }, [dispatch, selectedSeries, selectedAssessment]);

  const onSeriesSelected = (currSeries: Series): void => {
    setSelectedSeries(currSeries);
    setSelectedAssessment(undefined);
    setSelectedComponent(undefined);
  };

  const onAssessmentSelected = (assessment: PlagiarismAssessment): void => {
    setSelectedAssessment(assessment);
    setSelectedComponent(undefined);
  };

  const onCentreChange = (value: string): void => {
    setCentreId(value);
    if (!value) {
      setLearnerNumber('');
      setErrorMsg('');
    }
  };

  const onLearnerChange = (value: string): void => {
    const regexp = /^\d*$/;
    const matched = regexp.exec(value);

    if (errorMsg) {
      setErrorMsg('');
    }
    if (!matched) {
      setErrorMsg('Learner number must contain numbers only');
    }
    setLearnerNumber(value);
  };

  const history = useHistory();

  const routeChange = (): void => {
    const searchQuery = {
      seriesId: selectedSeries?.id,
      assessmentCode: selectedAssessment?.code,
      componentNumber: selectedComponent?.number,
      centreId: isEmpty(centreId) ? null : centreId,
      learnerNumber: isEmpty(learnerNumber) ? null : learnerNumber,
    };
    const params = qs.stringify({ searchQuery }, { skipNulls: true });
    const path = `malpracticeReports/${selectedSeries?.id}?${params.toString()}`;
    history.push(path);
  };

  const showComponentNotification = (): void => {
    if (!selectedAssessment) {
      dispatch(displayNotification('Please choose a syllabus for the component'));
    }
  };

  const showLearnerNotification = (): void => {
    if (!centreId) {
      dispatch(displayNotification('Please choose centre number for the candidate'));
    }
  };

  return (
    <div data-qa-id='page-priming-malpractice-reports'>
      <Headline>Series malpractice reports</Headline>
      <Paragraph>View Similarity Index Reports</Paragraph>
      <Title id='series'>Select series</Title>
      <StyledInputContainer>
        <Dropdown<Series>
          data={series}
          renderOption={renderSeries}
          renderSelectedOption={renderSeries}
          dataQaId='series-select'
          onChange={onSeriesSelected}
          value={selectedSeries || null}
          disabled={!!selectedSeries && !search.assessments}
          placeholder='Please choose a series'
          title='Select series'
          getOptionSelected={(option: Series, value: Series): boolean => option.id === value.id}
        />
      </StyledInputContainer>
      {selectedSeries &&
        (search.assessments ? (
          <>
            <Title>Select a syllabus and a component</Title>
            <StyledInputContainer>
              <DropdownContainer>
                <Dropdown<PlagiarismAssessment>
                  data={search.assessments}
                  renderOption={renderAssessments}
                  renderSelectedOption={renderAssessments}
                  dataQaId='assessments-select'
                  onChange={onAssessmentSelected}
                  value={selectedAssessment || null}
                  placeholder='Please choose a syllabus'
                  title='Select syllabus'
                  helperText='Optional'
                />
              </DropdownContainer>
              <DropdownContainer onClick={showComponentNotification}>
                <Dropdown<EntryComponent>
                  data={search.components || []}
                  renderOption={renderComponents}
                  renderSelectedOption={renderComponents}
                  dataQaId='component-select'
                  onChange={setSelectedComponent}
                  value={selectedComponent || null}
                  placeholder='Please choose a component'
                  title='Select component'
                  disabled={!search.components || !selectedAssessment}
                  helperText='Optional'
                />
              </DropdownContainer>
            </StyledInputContainer>
            <Title>Search for candidates reports</Title>
            <StyledInputContainer>
              <TextField
                dataQaId='search-centre'
                onChange={onCentreChange}
                placeholder='Centre number'
                helperText='Optional'
                value={centreId}
              />
              <div onClick={showLearnerNotification}>
                <TextField
                  dataQaId='search-candidate'
                  onChange={onLearnerChange}
                  placeholder='Candidate number'
                  helperText='Optional'
                  value={learnerNumber}
                  disabled={!centreId}
                  errorMsg={errorMsg}
                />
              </div>
              <Button onClick={routeChange} disabled={!!errorMsg} qaId='search-button'>
                Search reports
              </Button>
            </StyledInputContainer>
          </>
        ) : (
          <Loader />
        ))}
    </div>
  );
};
export default MalpracticeReports;
